import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { WhatsAppWidget } from "@/components/WhatsAppWidget";
import { InAppBrowserBanner } from "@/components/InAppBrowserBanner";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { PerformanceMonitor } from "@/components/PerformanceMonitor";
import { Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";

// Lazy load pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PaymentCallback = lazy(() => import("./pages/PaymentCallback"));
const Invoice = lazy(() => import("./pages/Invoice"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const CompleteAddress = lazy(() => import("./pages/CompleteAddress"));
const Login = lazy(() => import("./pages/Login"));
const Profile = lazy(() => import("./pages/Profile"));

// Admin pages (heavy, load on demand)
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminOrders = lazy(() => import("./pages/AdminOrders"));
const AdminPromoCodes = lazy(() => import("./pages/AdminPromoCodes"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));
const AdminLiveMonitor = lazy(() => import("./pages/AdminLiveMonitor"));
const AdminAbandonedCarts = lazy(() => import("./pages/AdminAbandonedCarts"));
const AdminIntegrations = lazy(() => import("./pages/AdminIntegrations"));
const AdminCustomers = lazy(() => import("./pages/AdminCustomers"));
const AdminSearchConsole = lazy(() => import("./pages/AdminSearchConsole"));
const AdminMerchantCenter = lazy(() => import("./pages/AdminMerchantCenter"));
const AdminInventory = lazy(() => import("./pages/AdminInventory"));
const AdminReports = lazy(() => import("./pages/AdminReports"));
const AdminTracking = lazy(() => import("./pages/AdminTracking"));
const AdminCreateInvoice = lazy(() => import("./pages/AdminCreateInvoice"));
const AdminFinance = lazy(() => import("./pages/AdminFinance"));
const AdminCafe = lazy(() => import("./pages/AdminCafe"));
const AdminPurchases = lazy(() => import("./pages/AdminPurchases"));
const AdminCommandCenter = lazy(() => import("./pages/AdminCommandCenter"));

// Cafe pages
const CafeMenu = lazy(() => import("./pages/CafeMenu"));
const CafeOrder = lazy(() => import("./pages/CafeOrder"));
const CafeCheckout = lazy(() => import("./pages/CafeCheckout"));
const CafePaymentCallback = lazy(() => import("./pages/CafePaymentCallback"));
const CafeOrderStatus = lazy(() => import("./pages/CafeOrderStatus"));
const CafeKitchen = lazy(() => import("./pages/CafeKitchen"));

const CafeMenuManager = lazy(() => import("./pages/CafeMenuManager"));
const Pos = lazy(() => import("./pages/Pos"));
const PosAdmin = lazy(() => import("./pages/PosAdmin"));

// Static pages
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const Blog = lazy(() => import("./pages/Blog"));
const Corporate = lazy(() => import("./pages/Corporate"));
const ContractTemplate = lazy(() => import("./pages/ContractTemplate"));
const DigitalContract = lazy(() => import("./pages/DigitalContract"));
const B2BPortal = lazy(() => import("./pages/B2BPortal"));
const FAQ = lazy(() => import("./pages/FAQ"));
const About = lazy(() => import("./pages/About"));
const Wholesale = lazy(() => import("./pages/Wholesale"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Gifting = lazy(() => import("./pages/Gifting"));
const BrewGuide = lazy(() => import("./pages/BrewGuide"));
const DripCoffee = lazy(() => import("./pages/DripCoffee"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-obha-beige" role="status" aria-label="جاري التحميل">
    <Loader2 className="h-12 w-12 animate-spin text-obha-navy" />
    <span className="sr-only">جاري التحميل</span>
  </div>
);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AuthProvider>
            <CartProvider>
              <AnalyticsTracker />
              <PerformanceMonitor />
              <Toaster />
              <Sonner position="bottom-center" richColors toastOptions={{ duration: 2500 }} />
              <ScrollToTop />
              <ErrorBoundary>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/checkout" element={<Checkout />} />
                  <Route path="/payment/callback" element={<PaymentCallback />} />
                  <Route path="/complete-address" element={<CompleteAddress />} />
                  <Route path="/invoice/:orderId" element={<Invoice />} />
                  <Route path="/track" element={<TrackOrder />} />
                  <Route path="/track-order" element={<TrackOrder />} /> {/* Redirect alias */}
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Login />} /> {/* Redirect to login */}
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/shop" element={<Index />} /> {/* Redirect to home */}
                  
                  {/* Admin Routes */}
                  <Route path="/admin" element={<AdminDashboard />} />
                  <Route path="/admin/live" element={<AdminLiveMonitor />} />
                  <Route path="/admin/orders" element={<AdminOrders />} />
                  <Route path="/admin/promo-codes" element={<AdminPromoCodes />} />
                  <Route path="/admin/customers" element={<AdminCustomers />} />
                  <Route path="/admin/customers/segments" element={<AdminCustomers />} />
                  <Route path="/admin/customers/behavior" element={<AdminCustomers />} />
                  {/* Marketing Section - Redirects */}
                  <Route path="/admin/marketing" element={<AdminPromoCodes />} />
                  <Route path="/admin/marketing/abandoned-carts" element={<AdminAbandonedCarts />} />
                  
                  {/* SEO Section - Redirects */}
                  <Route path="/admin/seo" element={<AdminSearchConsole />} />
                  <Route path="/admin/seo/search-console" element={<AdminSearchConsole />} />
                  <Route path="/admin/seo/merchant" element={<AdminMerchantCenter />} />
                  
                  {/* Other Admin Routes */}
                  <Route path="/admin/integrations" element={<AdminIntegrations />} />
                  <Route path="/admin/inventory" element={<AdminInventory />} />
                  <Route path="/admin/reports" element={<AdminReports />} />
                  <Route path="/admin/tracking" element={<AdminTracking />} />
                  <Route path="/admin/create-invoice" element={<AdminCreateInvoice />} />
                  <Route path="/admin/finance" element={<AdminFinance />} />
                  <Route path="/admin/cafe" element={<AdminCafe />} />
                  <Route path="/admin/purchases" element={<AdminPurchases />} />
                  <Route path="/admin/command-center" element={<AdminCommandCenter />} />
                  <Route path="/admin/settings" element={<AdminSettings />} />
                  <Route path="/admin/promocodes" element={<AdminPromoCodes />} /> {/* Legacy */}
                  
                  {/* Cafe Routes */}
                  <Route path="/cafe" element={<CafeMenu />} />
                  <Route path="/cafe/order" element={<CafeOrder />} />
                  <Route path="/cafe/checkout" element={<CafeCheckout />} />
                  <Route path="/cafe/payment/callback" element={<CafePaymentCallback />} />
                  <Route path="/cafe/order/status" element={<CafeOrderStatus />} />
                  <Route path="/cafe/kitchen" element={<CafeKitchen />} />
                  <Route path="/cafe/menu-manager" element={<CafeMenuManager />} />
                  <Route path="/pos" element={<Pos />} />
                  <Route path="/pos-admin" element={<PosAdmin />} />
                  
                  {/* Static Pages */}
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/return-policy" element={<ReturnPolicy />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/corporate" element={<Corporate />} />
                  <Route path="/wholesale" element={<Wholesale />} />
                  <Route path="/product" element={<ProductDetail />} />
                  <Route path="/gifting" element={<Gifting />} />
                  <Route path="/brew-guide" element={<BrewGuide />} />
                  <Route path="/drip-coffee" element={<DripCoffee />} />
                  <Route path="/contract" element={<ContractTemplate />} />
                  <Route path="/digital-contract" element={<DigitalContract />} />
                  <Route path="/b2b-portal" element={<B2BPortal />} />
                  <Route path="/faq" element={<FAQ />} />
                  <Route path="/about" element={<About />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              </ErrorBoundary>
              <InAppBrowserBanner />
              <WhatsAppWidget />
            </CartProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
